'use client';

import { Button as ButtonUI } from '@/components/common/inputs/button/button';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

export type ButtonProps = React.ComponentProps<'button'> & {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'outline' | 'neutral' | 'destructive' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  cursorPointer?: boolean;
  isActive?: boolean;
};

export default function Button({
  children,
  className,
  variant = 'default',
  size = 'default',
  cursorPointer = true,
  isActive = false,
  ...props
}: ButtonProps) {
  const commonStyles =
    'flex items-center justify-center gap-2 p-2 self-stretch shadow-none rounded';

  const buttonVariants = {
    default: cn(
      commonStyles,
      'rounded bg-gradient-to-r from-[#D0C1FE] to-[#7846FF]',
      'text-primary-foreground transition-[background-position,background-size] duration-200 ease-in-out bg-[size:200%_200%] bg-[position:100%_100%]',
      'hover:bg-[position:50%_50%] cursor-default',
      cursorPointer && 'cursor-pointer'
    ),
    outline: cn(
      commonStyles,
      'rounded border border-black/15 bg-white text-[#384250]',
      'hover:bg-accent hover:text-accent-foreground',
      'dark:bg-gray-800 dark:border-gray-700 dark:text-white',
      'dark:hover:bg-gray-700',
      cursorPointer && 'cursor-pointer'
    ),
    neutral: cn(
      commonStyles,
      'rounded border-none px-6 py-2 bg-[#F9FAFB] text-[#384250]',
      'flex justify-center items-center self-stretch',
      'hover:bg-accent hover:text-accent-foreground',
      'dark:bg-gray-700 dark:text-white',
      'dark:hover:bg-gray-600',
      cursorPointer && 'cursor-pointer'
    ),
    destructive: cn(
      commonStyles,
      'rounded border border-[rgba(180,35,24,0.20)] bg-[#FEF3F2] text-[#B42318]',
      'flex items-center px-6 py-2 self-stretch font-satoshi font-bold',
      'hover:bg-[#FFDFDD] hover:text-[#9B1D11] hover:border-[rgba(180,35,24,0.30)]',
      'dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
      'dark:hover:bg-red-900/30 dark:hover:text-red-300',
      cursorPointer && 'cursor-pointer'
    ),
    ghost: cn(
      commonStyles,
      'rounded border-none bg-transparent text-[#384250]',
      'hover:bg-accent hover:text-accent-foreground',
      'dark:text-gray-300',
      'dark:hover:bg-gray-800',
      cursorPointer && 'cursor-pointer'
    ),
  };

  return (
    <ButtonUI
      className={cn(
        buttonVariants[variant],
        isActive &&
          'bg-[#F5F0FF] text-[#7846FF] dark:bg-[#2D2D2D] dark:text-[#7846FF]',
        className
      )}
      size={size}
      {...props}
    >
      {children}
    </ButtonUI>
  );
}
